import { useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '@api'
import { showApiErrorToast, showSuccessToast } from '@utils'
import { useLanguage } from '@context'

export interface AudioResult {
  uri: string
  type: string
  name: string
  blob?: Blob
}

interface VoiceNoteResponse {
  transcription: string
  profileUpdates: Record<string, unknown>
  noteCreated: boolean
}

interface VoiceNoteParams {
  profileId: string
  audioResult: AudioResult
}

const sendVoiceNote = async ({ profileId, audioResult }: VoiceNoteParams): Promise<VoiceNoteResponse> => {
  const formData = new FormData()

  if (audioResult.blob) {
    // Web: append the Blob directly
    formData.append('audio', audioResult.blob, audioResult.name)
  } else {
    // Native: append using { uri, type, name } format
    formData.append('audio', { uri: audioResult.uri, type: audioResult.type, name: audioResult.name } as any)
  }

  const { data } = await api.post<VoiceNoteResponse>(`/ft_voice-note/${profileId}`, formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  })

  return data
}

export const useFtVoiceNote = () => {
  const queryClient = useQueryClient()
  const { t } = useLanguage()

  return useMutation<VoiceNoteResponse, Error, VoiceNoteParams>({
    mutationFn: sendVoiceNote,
    onSuccess: async (data) => {
      const messages: string[] = []
      if (Object.keys(data.profileUpdates).length > 0) messages.push(t('voiceNote.successProfileUpdated'))
      if (data.noteCreated) messages.push(t('voiceNote.successNoteCreated'))
      showSuccessToast(
        t('toast.success'),
        messages.length > 0 ? messages.join(' + ') : t('voiceNote.successProcessed'),
      )
      await queryClient.invalidateQueries({ queryKey: ['profile'] })
      await queryClient.invalidateQueries({ queryKey: ['notes'] })
      await queryClient.invalidateQueries({ queryKey: ['contacts'] })
    },
    onError: (error) => {
      showApiErrorToast(t, error)
    },
  })
}
